const API = '/api/remote-control/sessions';
export const DIRECT_CONNECTION_TIMEOUT_MS = 10000;

export async function pairingRequest(path, { token, signal, ...options } = {}) {
    const requestAbort = new AbortController();
    let timedOut = false;
    const abort = () => requestAbort.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
    const timeout = setTimeout(() => {
        timedOut = true;
        requestAbort.abort();
    }, 8000);
    try {
        const response = await fetch(`${API}${path}`, {
            ...options,
            signal: requestAbort.signal,
            cache: 'no-store',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
        if (response.status === 204) return null;
        const data = await response.json();
        if (!response.ok) {
            const error = new Error(data.error || 'Could not pair the viewing screen.');
            error.status = response.status;
            throw error;
        }
        return response.status === 202 ? null : data;
    } catch (error) {
        if (timedOut) throw new Error('The server connection timed out. Please try again.');
        throw error;
    } finally {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', abort);
    }
}

export function createPeer() {
    if (!window.RTCPeerConnection) throw new Error('This browser does not support remote viewing.');
    // Chrome may expose only its default interface and an mDNS host name.
    // STUN supplies a reachable candidate without relaying playback traffic.
    return new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
}

export function gatherDescription(peer, signal) {
    return new Promise((resolve, reject) => {
        let settled = false;
        let quiet;
        const hasCandidate = () => /^a=candidate:/m.test(peer.localDescription?.sdp || '');
        const hasStunCandidate = () => /^a=candidate:.* typ srflx(?: |\r?$)/m.test(peer.localDescription?.sdp || '');
        const finish = (error) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            clearTimeout(quiet);
            peer.removeEventListener('icegatheringstatechange', check);
            peer.removeEventListener('icecandidate', candidate);
            signal.removeEventListener('abort', abort);
            if (error) reject(error);
            else if (!hasCandidate()) reject(new Error('No direct connection address was found.'));
            else resolve(peer.localDescription.toJSON());
        };
        const check = () => {
            if (peer.iceGatheringState === 'complete') finish();
            else if (hasStunCandidate()) {
                clearTimeout(quiet);
                quiet = setTimeout(() => finish(), 250);
            }
        };
        const candidate = () => check();
        const abort = () => finish(new DOMException('Pairing cancelled', 'AbortError'));
        // Waiting for every interface/STUN attempt can take tens of seconds.
        // Use the candidates already gathered; keep HTTP fallback if none work.
        const timeout = setTimeout(() => finish(), 3000);
        peer.addEventListener('icegatheringstatechange', check);
        peer.addEventListener('icecandidate', candidate);
        signal.addEventListener('abort', abort, { once: true });
        if (signal.aborted) abort();
        else check();
    });
}

export function sendMessage(channel, message) {
    if (channel?.readyState === 'open') {
        channel.send(JSON.stringify(message));
    }
}

export function readMessage(event) {
    if (typeof event.data !== 'string' || event.data.length > 4096) return null;
    try {
        return JSON.parse(event.data);
    } catch {
        return null;
    }
}
